import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useOrgSlug } from "@/common/artemis-operations/organization";
import { useWorkspaces } from "@/common/artemis-operations/workspaces";
import { FullSpin } from "@/components/base";

export const Route = createFileRoute("/_authenticated/import-template")({
	validateSearch: (search: Record<string, unknown>): { url?: string } => ({
		url: typeof search.url === "string" ? search.url : undefined,
	}),
	component: ImportTemplate,
});

/**
 * Org/workspace-less landing for an external "Install in Artemis" link
 * (`?url=<templateUrl>`), since the linking page cannot know which org or
 * workspace the visitor will land in. Resolves the org, then forwards straight
 * into the one workspace the user has, or onto the existing workspace picker
 * when they have more than one.
 */
function ImportTemplate() {
	const { url } = Route.useSearch();
	const orgSlug = useOrgSlug();
	const { data: workspaces, isPending } = useWorkspaces();

	if (!orgSlug || isPending) {
		return (
			<div style={{ height: "100vh" }}>
				<FullSpin />
			</div>
		);
	}

	if (!url) {
		return <Navigate to="/$orgSlug/workspaces" params={{ orgSlug }} replace />;
	}

	const rows = (workspaces ?? []).filter(
		(w): w is NonNullable<typeof w> => w != null,
	);

	if (rows.length === 1) {
		return (
			<Navigate
				to="/$orgSlug/workspaces/$workspaceId/task-templates"
				params={{ orgSlug, workspaceId: rows[0].id }}
				search={{ importTemplate: url }}
				replace
			/>
		);
	}

	return (
		<Navigate
			to="/$orgSlug/workspaces"
			params={{ orgSlug }}
			search={{ importTemplate: url }}
			replace
		/>
	);
}
